import { PolylineProps } from '@editor/interfaces/canvas'
import { CanvasConfig } from '@editor/interfaces/editor'
import { DepthLabelVisibility } from '@editor/interfaces/editorSettings'
import { InspectionItem } from '@editor/interfaces/inspection'
import { selectIsAdjustingLabelColors } from '@editor/pages/projects/editor/store/selectors'
import { getDepthTypeColor } from '@editor/services/DepthEstimation'
import { getCenterOfTwoPoints } from '@editor/services/Editor'
import { meterToMilimeterWhole } from '@editor/services/Util'
import { useAppSelector } from '@editor/store/app'
import { Vector3 } from 'three'

const getDistanceLabels = (
  inspectionItem: InspectionItem,
  selectedElementIds: string[],
  hiddenElementIds: string[],
  hoveredElementId: string,
  isAdjustingColors: boolean,
  labelTextColor: string,
  labelBgColor: string,
  anchorColor: string,
  isLabelSettingsVisible: boolean,
  show: DepthLabelVisibility | undefined,
) => {
  const minDistances = inspectionItem.plane_to_cylinders_distance?.individual_distances
  const maxDistances = inspectionItem.plane_to_cylinders_distance?.max_individual_distances

  const commonProps: Partial<PolylineProps> = {
    anchorProps: {
      color: anchorColor,
    },
    vertexAnchors: {},
    label: {
      styles: {
        color: labelTextColor,
        backgroundColor: labelBgColor,
      },
      isAdjustingColors,
    },
  }

  // A suffix turned off in the かぶり厚 display settings is excluded entirely:
  // it never renders, and hover/selection can't reveal it (hatsuly-app issue
  // #2646). A missing flag defaults to shown (older configs).
  const isExcluded = (suffix: string) => show?.[suffix as keyof DepthLabelVisibility] === false

  const isVisible = (item: InspectionItem, suffix: string) => {
    if (isExcluded(suffix)) return false

    const isHidden = hiddenElementIds.includes(`${item.inspection_item_id}-${suffix}`)
    if (
      (!isHidden && selectedElementIds.includes(`${item.inspection_item_id}-${suffix}`)) ||
      hoveredElementId === `${item.inspection_item_id}-${suffix}`
    ) {
      return true
    }

    return !isHidden
  }

  // Label are only visible when selected or hovered
  const isLabelVisible = (item: InspectionItem, suffix: string) =>
    !isExcluded(suffix) &&
    isLabelSettingsVisible &&
    (selectedElementIds.includes(`${item.inspection_item_id}-${suffix}`) ||
      hoveredElementId === `${item.inspection_item_id}-${suffix}`)

  const minDistanceLabels = minDistances
    ? minDistances.map<PolylineProps>((distance, index) => ({
        ...commonProps,
        id: `${inspectionItem.inspection_item_id}-min-${index}`,
        visible: isVisible(inspectionItem, `min`),
        label: {
          ...(commonProps.label instanceof Object ? commonProps.label : {}),
          label: `${meterToMilimeterWhole(distance.distance)}mm(最小)`,
          visible: isLabelVisible(inspectionItem, `min`),
        },
        points: distance.positions_for_distance,
      }))
    : []
  const maxDistanceLabels = maxDistances
    ? maxDistances.map<PolylineProps>((distance, index) => ({
        ...commonProps,
        id: `${inspectionItem.inspection_item_id}-max-${index}`,
        visible: isVisible(inspectionItem, `max`),
        label: {
          ...(commonProps.label instanceof Object ? commonProps.label : {}),
          label: `${meterToMilimeterWhole(distance.distance)}mm(最大)`,
          visible: isLabelVisible(inspectionItem, `max`),
        },
        points: distance.positions_for_distance,
      }))
    : []
  const averageDistanceLabels =
    maxDistances?.length && maxDistances.length === minDistances?.length
      ? maxDistances.map<PolylineProps>((distance, index) => ({
          ...commonProps,
          id: `${inspectionItem.inspection_item_id}-avg-${index}`,
          visible: isVisible(inspectionItem, `avg`),
          label: {
            ...(commonProps.label instanceof Object ? commonProps.label : {}),
            label: `${meterToMilimeterWhole((distance.distance + minDistances[index].distance) / 2)}mm(平均)`,
            visible: isLabelVisible(inspectionItem, `avg`),
          },
          points: distance.positions_for_distance.map((point, i) => {
            const center = getCenterOfTwoPoints(
              new Vector3(...point),
              new Vector3(...minDistances[index].positions_for_distance[i]),
            )
            return [center.x, center.y, center.z]
          }),
        }))
      : []
  return minDistanceLabels.concat(maxDistanceLabels, averageDistanceLabels)
}

export default function useMainCanvas(): CanvasConfig {
  // Store
  const isAdjustingLabelColors = useAppSelector(selectIsAdjustingLabelColors)
  const depthDisplaySettings = useAppSelector((state) => state.editorSettings.display.depth)
  const currentAdjustingKey = useAppSelector((state) => state.toolUtil.core.currentAdjustingKey)

  const depthEstimationTypes = useAppSelector((state) => state.toolDepthEstimation.depthEstimationTypes)
  const hiddenElementIds = useAppSelector((state) => state.toolUtil.core.hiddenElementIds)
  const selectedElementIds = useAppSelector((state) => state.toolUtil.core.selectedElementIds)
  const hoveredElementId = useAppSelector((state) => state.toolUtil.core.hoveredElementId)

  return {
    objects: {
      polylines: [
        // Top side
        ...(depthEstimationTypes[2].inspectionItems
          ? depthEstimationTypes[2].inspectionItems
              .map<PolylineProps[]>((item) =>
                getDistanceLabels(
                  item,
                  selectedElementIds,
                  hiddenElementIds,
                  hoveredElementId,
                  isAdjustingLabelColors &&
                    currentAdjustingKey !== undefined &&
                    ['depth.upper.label.text_color', 'depth.upper.label.bg_color'].includes(currentAdjustingKey),
                  depthDisplaySettings.upper.label.text_color,
                  depthDisplaySettings.upper.label.bg_color,
                  getDepthTypeColor(3),
                  depthDisplaySettings.upper.label.visible === true,
                  depthDisplaySettings.upper.show,
                ),
              )
              .flat()
              .filter(Boolean)
          : []),
        // Bottom side
        ...(depthEstimationTypes[3].inspectionItems
          ? depthEstimationTypes[3].inspectionItems
              .map<PolylineProps[]>((item) =>
                getDistanceLabels(
                  item,
                  selectedElementIds,
                  hiddenElementIds,
                  hoveredElementId,
                  isAdjustingLabelColors &&
                    currentAdjustingKey !== undefined &&
                    ['depth.lower.label.text_color', 'depth.lower.label.bg_color'].includes(currentAdjustingKey),
                  depthDisplaySettings.lower.label.text_color,
                  depthDisplaySettings.lower.label.bg_color,
                  getDepthTypeColor(4),
                  depthDisplaySettings.lower.label.visible === true,
                  depthDisplaySettings.lower.show,
                ),
              )
              .flat()
              .filter(Boolean)
          : []),
      ],
    },
  }
}
